import {
  memo,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router'

import {isSecondaryQs} from '../../consts/qs'
import {useEvent} from '../hooks/useEvent'

const eventDispatcher = new EventTarget()

export type NavigateOptions = {
  replace?: boolean
}
type NavigateFunction = (to: string, options?: NavigateOptions) => void
type Location = {
  pathname: string
  search: string
  hash: string
}
type URLSearchParamsInit = URLSearchParams
type SetSearchParamsFunction = (nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit), options?: NavigateOptions) => void

type RouterState = {
  location: Location
  navigate: NavigateFunction
  params: Record<string, string | undefined>
}
const defaultState = {
  location: window.location,
  navigate: (to: string) => {
    window.location.href = to
  },
  params: {},
}
const routerState: RouterState = {...defaultState}

const useRouteChange = (onRouteChange: () => void): void => {
  useEffect(() => {
    // using the closure is required to avoid raceCondition between removeEventListener (in queueMicrotask) and addEventListener
    // Read synchronously (no queueMicrotask): one render apart, an auto-reset effect runs with fresh `items` and stale `value`.
    const closure = () => {
      onRouteChange()
    }
    eventDispatcher.addEventListener('route-change', closure)

    return () => {
      queueMicrotask(() => {
        eventDispatcher.removeEventListener('route-change', closure)
      })
    }
  }, [onRouteChange])
}

type NavigationNavigateOptions = {
  replace?: boolean
  preserveSearch?: boolean | 'primary'
  newWindow?: boolean
}
type NavigationNavigate = (to: string, options?: NavigationNavigateOptions) => void
export const buildUrl = (to: string, options: NavigationNavigateOptions): [boolean, URL] => {
  const currentUrl = new URL(window.location.href)
  if (!currentUrl.pathname.endsWith('/')) {
    currentUrl.pathname += '/'
  }

  const targetUrl = to.startsWith('https://') ? new URL(to) : new URL(to, currentUrl.href)
  const isExternal = targetUrl.origin !== currentUrl.origin

  if (!isExternal && options.preserveSearch) {
    const currentSearch = new URLSearchParams(window.location.search)
    for (const [key, value] of currentSearch.entries()) {
      if (options.preserveSearch === 'primary' && isSecondaryQs(key)) {
        continue
      }

      if (!targetUrl.searchParams.has(key)) {
        targetUrl.searchParams.set(key, value)
      }
    }
  }

  return [isExternal, targetUrl]
}

export const useStableNavigate = (): NavigationNavigate => {
  return useEvent((to: string, options: NavigationNavigateOptions = {}): void => {
    const [isExternal, targetUrl] = buildUrl(to, options)

    if (isExternal || options.newWindow) {
      window.open(targetUrl.href, '_blank', 'noopener,noreferrer')
    } else {
      routerState.navigate(targetUrl.pathname + targetUrl.search + targetUrl.hash, {
        replace: options?.replace,
      })
    }
  })
}
export const useStablePathname = (): string => {
  const [pathname, setPathname] = useState(() => routerState.location.pathname)

  const checkPathname = useEvent((): void => {
    setPathname(routerState.location.pathname)
  })
  useRouteChange(checkPathname)

  return pathname
}
export const useStableSearch = (): string => {
  const [search, setSearch] = useState(() => routerState.location.search)

  const checkSearch = useEvent((): void => {
    setSearch(routerState.location.search)
  })
  useRouteChange(checkSearch)

  return search
}
export const useStableParamValue = (key: string): string | undefined => {
  return useStableParamValues([key])[key]
}

/** Re-renders only when `select`'s result changes (Object.is), not on every change of the param. */
export const useStableParamSelector = <T,>(key: string, select: (value: string | undefined) => T): T => {
  const readSelected = useEvent((): T => select(routerState.params[key]))
  const [selected, setSelected] = useState<T>(readSelected)
  const current = readSelected()
  if (!Object.is(selected, current)) {
    setSelected(() => current)
  }

  const checkSelected = useEvent((): void => {
    const next = readSelected()
    setSelected((prev) => Object.is(prev, next) ? prev : next)
  })
  useRouteChange(checkSelected)

  return current
}

export const useStableParamValues = (keys: string[]): Record<string, string> => {
  const keysSignature = JSON.stringify(keys)
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to memoize the values based on the current form state and querystring
  const stableKeys = useMemo(() => {
    return new Set<string>(keys.toSorted())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysSignature])

  const createParams = useEvent((): Record<string, string> => {
    const newParams = {} as Record<string, string>
    const searchParams = routerState.params
    for (const key of stableKeys.values()) {
      if (searchParams[key] !== undefined) {
        newParams[key] = searchParams[key]
      }
    }

    return newParams
  })

  const [params, setParams] = useState<Record<string, string>>(() => createParams())

  const checkParams = useEvent((): void => {
    const newParams = createParams()
    setParams((prev) => {
      for (const key of stableKeys.values()) {
        if (prev[key] !== newParams[key]) {
          return newParams
        }
      }

      return prev
    })
  })

  useRouteChange(checkParams)

  return params
}
type DispatchWithOptions<A> = (value: A, navigateOpts?: NavigateOptions) => void
const sanitizeSearchParamValue = <T extends string>(value: string | null): T | undefined => {
  if (value === null) {
    return undefined
  }

  return (value) as T
}
export const useStableSearchParam = <T extends string>(key: string, navigateOpts?: NavigateOptions): [T | undefined, DispatchWithOptions<SetStateAction<T | undefined>>] => {
  const value = useStableSearchParamValue<T>(key)
  const setSearchParams = useStableSetSearchParams()
  const globalNavigateOpts = navigateOpts

  const setter = useEvent((value: SetStateAction<T | undefined>, navigateOpts?: NavigateOptions) => {
    setSearchParams((prev: URLSearchParams) => {
      const newValue: T | undefined | null = typeof value === 'function' ? value(sanitizeSearchParamValue(prev.get(key))) : value
      const newParams = new URLSearchParams(prev)

      if (newValue === undefined || newValue === null) {
        if (!prev.has(key)) {
          return prev
        }
        newParams.delete(key)
      } else if (prev.get(key) === newValue.toString()) {
        return prev
      } else {
        newParams.set(key, newValue.toString())
      }

      return newParams
    }, navigateOpts ?? globalNavigateOpts)
  })

  return [value, setter]
}
export const useStableSearchParamValue = <T extends string>(key: string): T | undefined => {
  const value = useStableSearchParamValues([key]).get(key)

  return sanitizeSearchParamValue(value)
}
export const useStableSearchParamValues = (keys: string[]): URLSearchParams => {
  const keysSignature = JSON.stringify(keys)
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to memoize the values based on the current form state and querystring
  const stableKeys = useMemo(() => {
    return new Set<string>(keys.toSorted())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysSignature])

  const createUrlParams = useEvent((): URLSearchParams => {
    const newParams = new URLSearchParams()
    const searchParams = new URLSearchParams(routerState.location.search)
    for (const key of stableKeys.values()) {
      if (searchParams.has(key)) {
        newParams.set(key, searchParams.get(key) || '')
      }
    }

    return newParams
  })

  const [params, setParams] = useState<URLSearchParams>(() => createUrlParams())

  const checkUrlParams = useEvent((): void => {
    const newParams = createUrlParams()
    setParams((prev) => {
      if (prev.toString() === newParams.toString()) {
        return prev
      }
      return newParams
    })
  })

  useRouteChange(checkUrlParams)

  return params
}
export const useStableSetSearchParams = (): SetSearchParamsFunction => {
  return useEvent((nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit), options?: NavigateOptions): void => {
    if (typeof nextInit === 'function') {
      nextInit = nextInit(new URLSearchParams(routerState.location.search))
    } else if (nextInit === undefined) {
      nextInit = new URLSearchParams(routerState.location.search)
    }
    const qs = nextInit.toString()
    const search = qs ? `?${qs}` : ''
    if (search === routerState.location.search) {
      return
    }

    routerState.location = {...routerState.location, search}
    const {pathname, hash} = routerState.location
    routerState.navigate(`${pathname}${search}${hash}`, options)

    // Dispatched in the caller's own batch: react-router commits the location one render later.
    eventDispatcher.dispatchEvent(new CustomEvent('route-change', {detail: {location: routerState.location}}))
  })
}

const StableRouterSynchronizer = memo(function StableRouterSynchronizer() {
  // eslint-disable-next-line react-hooks/immutability
  routerState.navigate = useNavigate()
  const location = useLocation()
  // eslint-disable-next-line react-hooks/immutability
  routerState.location = location
  const params = useParams()
  // eslint-disable-next-line react-hooks/immutability
  routerState.params = params

  // AFTER commit, never during render: a listener's setState fired mid-render is deferred work.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `params` is intentionally a dep so the effect re-fires on params change even when location reference is stable (see commit 23e706d)
  useLayoutEffect(() => {
    eventDispatcher.dispatchEvent(new CustomEvent('route-change', {
      detail: {location},
    }))
  }, [location, params])

  return null
})
export const StableRouter = memo(function StableRouter({children}: PropsWithChildren) {
  return <>
    <StableRouterSynchronizer/>
    {children}
  </>
})
